import React, { useState, useEffect } from 'react';
import { getApiBaseUrl, getWebSocketBaseUrl } from '../utils/api';
import { LiveEventView } from './LiveEventView';

// Real WebSocket hook for connecting to the UserSessionDO
function useWebSocket(url) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        if (!url) return;

        console.log(`Connecting WebSocket to: ${url}`);

        // For now, we'll simulate WebSocket connection since the actual implementation
        // would depend on your UserSessionDO WebSocket handling
        // In a real implementation, you would do:
        // const ws = new WebSocket(url);

        // Simulate receiving an initial leaderboard
        const mockInitialData = [
            { participantId: 'p_jane_doe', horseName: 'Trigger', finalTime: 15.21 },
            { participantId: 'p_john_smith', horseName: 'Lightning', finalTime: 19.98 },
        ];
        setData(mockInitialData);
        setConnected(true);

        // Simulate a real-time update after 5 seconds
        const timeoutId = setTimeout(() => {
            console.log("WebSocket receiving update...");
            const mockUpdate = [
                { participantId: 'p_jane_doe', horseName: 'Trigger', finalTime: 15.21 },
                { participantId: 'p_sally_jones', horseName: 'Comet', finalTime: 15.88 }, // New run
                { participantId: 'p_john_smith', horseName: 'Lightning', finalTime: 19.98 },
            ];
            setData(mockUpdate);
        }, 5000);

        // Cleanup function
        return () => {
            console.log("Closing WebSocket connection.");
            clearTimeout(timeoutId);
            setConnected(false);
        };
    }, [url]);

    return { data, error, connected };
}

export function SpectatorView() {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [eventStateId, setEventStateId] = useState('');
    const [viewMode, setViewMode] = useState('live'); // 'live' or 'leaderboard'

    // Fetch available events on component mount
    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`${getApiBaseUrl()}/api/events`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const events = await response.json();
                setEvents(events);

                if (events.length > 0) {
                    setSelectedEvent(events[0]);
                }
            } catch (err) {
                setError(`Failed to fetch events: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, []);

    // Fetch classes when an event is selected
    useEffect(() => {
        if (!selectedEvent) {
            setClasses([]);
            setSelectedClass(null);
            return;
        }

        const fetchClasses = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`${getApiBaseUrl()}/api/events/${selectedEvent.id}/classes`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const classesData = await response.json();

                // Convert the classes object to an array format for easier handling
                const classesArray = Object.entries(classesData).map(([name, classId]) => ({
                    id: classId,
                    name: name
                }));

                setClasses(classesArray);

                if (classesArray.length > 0) {
                    setSelectedClass(classesArray[0]);
                } else {
                    setSelectedClass(null);
                }
            } catch (err) {
                setError(`Failed to fetch classes: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchClasses();
    }, [selectedEvent]);

    // Construct WebSocket URL for real-time updates
    const baseUrl = getWebSocketBaseUrl();
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = selectedClass ?
        (baseUrl ? `${baseUrl}/api/sessions?classId=${selectedClass.id}` : `${protocol}//${window.location.host}/api/sessions?classId=${selectedClass.id}`)
        : null;
    const { data: leaderboard, error: wsError, connected } = useWebSocket(wsUrl);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <span className="text-primary text-lg">👁️</span>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-foreground">Spectator View</h2>
                        <p className="text-muted-foreground">Watch live barrel racing results in real-time</p>
                    </div>
                </div>

                {/* Signup Link */}
                {selectedEvent && (
                    <a
                        href={`/signup/${selectedEvent.id}`}
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
                    >
                        🏇 Sign Up for Event
                    </a>
                )}



                {/* View Mode Toggle */}
                <div className="flex space-x-1">
                    <button
                        onClick={() => setViewMode('live')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${viewMode === 'live'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                            }`}
                    >
                        Live Event
                    </button>
                    <button
                        onClick={() => setViewMode('leaderboard')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${viewMode === 'leaderboard'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                            }`}
                    >
                        Leaderboard
                    </button>
                </div>
            </div>

            {/* Error Alerts */}
            {error && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-destructive">⚠️</span>
                        <p className="text-sm text-destructive">{error}</p>
                    </div>
                </div>
            )}

            {wsError && (
                <div className="rounded-lg border border-orange-500/50 bg-orange-500/10 p-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-orange-600">🔌</span>
                        <p className="text-sm text-orange-600">WebSocket Error: {wsError}</p>
                    </div>
                </div>
            )}

            {/* Live Event View */}
            {viewMode === 'live' && (
                <div className="space-y-6">
                    {/* Event State ID Input */}
                    <div className="rounded-lg border bg-card p-6 shadow-sm">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                                <span className="text-purple-600 text-sm">🔗</span>
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">Connect to Live Event</h3>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="eventStateId" className="text-sm font-medium text-foreground">
                                    Event State ID
                                </label>
                                <input
                                    id="eventStateId"
                                    type="text"
                                    value={eventStateId}
                                    onChange={e => setEventStateId(e.target.value)}
                                    placeholder="Enter event state ID to connect to live event..."
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Ask the event producer for the Event State ID to connect to the live event.
                            </p>
                        </div>
                    </div>

                    {/* Live Event View Component */}
                    {eventStateId && <LiveEventView eventStateId={eventStateId} />}
                </div>
            )}

            {/* Leaderboard View */}
            {viewMode === 'leaderboard' && (
                <div className="space-y-6">
                    {/* Event Selection */}
                    <div className="rounded-lg border bg-card p-6 shadow-sm">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <span className="text-blue-600 text-sm">🎪</span>
                            </div>
                            <h3 className="text-lg font-semibold text-foreground">Select an Event</h3>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="eventSelect" className="text-sm font-medium text-foreground">
                                    Choose Event
                                </label>
                                <select
                                    id="eventSelect"
                                    value={selectedEvent?.id || ''}
                                    onChange={e => {
                                        const event = events.find(evt => evt.id === e.target.value);
                                        setSelectedEvent(event);
                                        setSelectedClass(null);
                                    }}
                                    disabled={loading}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <option value="">Select an event...</option>
                                    {events.map(event => (
                                        <option key={event.id} value={event.id}>{event.name}</option>
                                    ))}
                                </select>
                            </div>

                            {selectedEvent && (
                                <div className="rounded-lg bg-muted/50 p-4">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-muted-foreground">📅</span>
                                        <div>
                                            <p className="text-sm font-medium text-foreground">{selectedEvent.name}</p>
                                            {selectedEvent.date && (
                                                <p className="text-xs text-muted-foreground">Date: {selectedEvent.date}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Class Selection */}
                    {selectedEvent && (
                        <div className="rounded-lg border bg-card p-6 shadow-sm">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                                    <span className="text-green-600 text-sm">🏆</span>
                                </div>
                                <h3 className="text-lg font-semibold text-foreground">Select a Class to Watch</h3>
                            </div>

                            <div className="space-y-4">
                                {loading ? (
                                    <div className="flex items-center space-x-2 text-muted-foreground">
                                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="text-sm">Loading classes...</span>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <label htmlFor="classSelect" className="text-sm font-medium text-foreground">
                                            Choose Class
                                        </label>
                                        <select
                                            id="classSelect"
                                            value={selectedClass?.id || ''}
                                            onChange={e => {
                                                const classObj = classes.find(cls => cls.id === e.target.value);
                                                setSelectedClass(classObj);
                                            }}
                                            disabled={loading}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <option value="">Select a class...</option>
                                            {classes.map(classObj => (
                                                <option key={classObj.id} value={classObj.id}>
                                                    {classObj.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {classes.length === 0 && !loading && (
                                    <div className="rounded-lg bg-muted/50 p-4 text-center">
                                        <p className="text-sm text-muted-foreground italic">No classes found for this event.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Live Leaderboard */}
                    {selectedClass && (
                        <div className="rounded-lg border bg-card p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center space-x-2">
                                    <div className="h-6 w-6 rounded-full bg-purple-500/10 flex items-center justify-center">
                                        <span className="text-purple-600 text-sm">📊</span>
                                    </div>
                                    <h3 className="text-lg font-semibold text-foreground">
                                        Live Leaderboard: {selectedClass.name}
                                    </h3>
                                </div>

                                {/* Connection Status */}
                                <div className="flex items-center space-x-2">
                                    <div className={`h-2 w-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                    <span className={`text-xs font-medium ${connected ? 'text-green-600' : 'text-red-600'}`}>
                                        {connected ? 'Connected' : 'Connecting...'}
                                    </span>
                                </div>
                            </div>

                            {!leaderboard ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="flex items-center space-x-2 text-muted-foreground">
                                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="text-sm">Loading leaderboard...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="overflow-hidden rounded-lg border">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-muted/50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Rank</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Participant</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Horse</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Time</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {leaderboard.map((run, index) => (
                                                    <tr key={`${run.participantId}-${run.horseName}`} className="hover:bg-muted/50 transition-colors">
                                                        <td className="px-4 py-3 text-sm">
                                                            <div className="flex items-center space-x-2">
                                                                {index === 0 && <span className="text-yellow-500">🥇</span>}
                                                                {index === 1 && <span className="text-gray-400">🥈</span>}
                                                                {index === 2 && <span className="text-amber-600">🥉</span>}
                                                                <span className="font-medium">{index + 1}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-foreground">{run.participantId}</td>
                                                        <td className="px-4 py-3 text-sm text-foreground">{run.horseName}</td>
                                                        <td className="px-4 py-3 text-sm font-mono font-medium text-foreground">
                                                            {run.finalTime.toFixed(3)}s
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
